import React, { useEffect, useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from '../utils/axios'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import SeoHead from './SeoHead'
import taaounityMark from '../logos/taaounity-mark.png'
import { buildAbsoluteUrl } from '../utils/seo'
import { InteractiveStarRating } from './reviews/StarRating'
import './OrderReceiptPage.css'
import './CooperativeReviewsPage.css'

function ReceiptBrandMark() {
  const [src, setSrc] = useState('')

  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const scale = 2
      canvas.width = Math.max(1, img.naturalWidth * scale)
      canvas.height = Math.max(1, img.naturalHeight * scale)
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const pixels = image.data
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i]
        const g = pixels[i + 1]
        const b = pixels[i + 2]
        if (Math.max(r, g, b) < 48) {
          pixels[i + 3] = 0
        } else {
          pixels[i] = 107
          pixels[i + 1] = 107
          pixels[i + 2] = 115
          pixels[i + 3] = 255
        }
      }
      ctx.putImageData(image, 0, 0)
      setSrc(canvas.toDataURL('image/png'))
    }
    img.src = taaounityMark
  }, [])

  if (!src) return <span className="take-receipt-mark-img" aria-hidden />
  return <img src={src} alt="" className="take-receipt-mark-img" />
}

export default function OrderReceiptPage() {
  const { token } = useParams()
  const [receipt, setReceipt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [shareNote, setShareNote] = useState('')
  const receiptRef = useRef(null)
  const [reviewRating, setReviewRating] = useState(0)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewSubmitting, setReviewSubmitting] = useState(false)
  const [reviewMessage, setReviewMessage] = useState(null)

  useEffect(() => {
    if (!token) {
      setError('Lien invalide')
      setLoading(false)
      return
    }
    setLoading(true)
    axios
      .get(`/api/marketplace/receipt/${token}/`)
      .then((res) => {
        setReceipt(res.data)
        setError(null)
      })
      .catch((err) => {
        if (err.response?.status !== 404) {
          setError('Erreur de chargement')
          setReceipt(null)
          return
        }
        return axios.get(`/api/shop/orders/receipt/${token}/`).then((res) => {
          setReceipt(res.data)
          setError(null)
        })
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? 'Reçu introuvable' : 'Erreur de chargement')
        setReceipt(null)
      })
      .finally(() => setLoading(false))
  }, [token])

  const formatDate = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateWithTz = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  }

  const formatNum = (n) => Math.round(Number(n) || 0)
  const receiptTotal = receipt ? Math.round(Number(receipt.subtotal) + Number(receipt.delivery_fee || 0)) : 0
  const receiptRefDisplay = receipt?.receipt_reference || (receipt ? `TA${String(receipt.order_number).padStart(5, '0')}` : '')
  const catalogUrl = receipt?.catalog_url
    ? (receipt.catalog_url.startsWith('http') ? receipt.catalog_url : `${window.location.origin}${receipt.catalog_url}`)
    : receipt?.coop_slug
      ? `${window.location.origin}/${receipt.coop_slug}`
      : null
  const isShopReceipt = receipt?.source === 'shop'

  const downloadPDF = () => {
    if (!receipt || !receiptRef.current) return
    const el = receiptRef.current
    html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      ignoreElements: (node) => node.classList && node.classList.contains('no-print'),
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF('p', 'mm', 'a4')
        const pageW = pdf.internal.pageSize.getWidth()
        const pageH = pdf.internal.pageSize.getHeight()
        const margin = 10
        const imgW = pageW - 2 * margin
        const imgH = (canvas.height * imgW) / canvas.width
        let heightLeft = imgH
        let position = margin
        pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH)
        heightLeft -= (pageH - 2 * margin)
        while (heightLeft > 0) {
          pdf.addPage()
          position = margin - (imgH - heightLeft)
          pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH)
          heightLeft -= (pageH - 2 * margin)
        }
        const fileName = `recu-${receipt.receipt_reference || receiptRefDisplay}-${(receipt.created_at || '').slice(0, 10)}.pdf`
        pdf.save(fileName)
      })
      .catch((err) => {
        console.error('PDF generation failed:', err)
        alert('Impossible de générer le PDF. Réessayez.')
      })
  }

  const handlePrint = () => window.print()

  const shareReceipt = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}`
    const title = `Reçu ${receiptRefDisplay}`
    const text = `Reçu de commande ${receiptRefDisplay}${receipt?.cooperative_name ? ` — ${receipt.cooperative_name}` : ''}`
    setShareNote('')
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl })
        return
      }
    } catch (err) {
      if (err?.name === 'AbortError') return
    }
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShareNote('Lien du reçu copié.')
    } catch {
      setShareNote(shareUrl)
    }
  }

  const handleSubmitReview = async (e) => {
    e.preventDefault()
    if (!receipt?.can_leave_review) return
    if (!reviewRating) {
      setReviewMessage({ type: 'error', text: 'Veuillez choisir une note.' })
      return
    }
    setReviewSubmitting(true)
    setReviewMessage(null)
    try {
      await axios.post(isShopReceipt ? '/api/shop/reviews/' : '/api/marketplace/reviews/', {
        receipt_token: token,
        rating: reviewRating,
        comment: reviewComment.trim(),
        author_name: receipt.guest?.full_name || 'Client',
      })
      setReviewMessage({
        type: 'success',
        text: isShopReceipt
          ? 'Merci ! Votre avis sera publié après validation.'
          : 'Merci ! Votre avis sera publié après validation par la coopérative.',
      })
      setReceipt((prev) => (prev ? { ...prev, has_review: true, can_leave_review: false } : prev))
    } catch (err) {
      setReviewMessage({
        type: 'error',
        text: err.response?.data?.error || 'Impossible d\'envoyer votre avis.',
      })
    } finally {
      setReviewSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="receipt-page">
        <SeoHead noindex title="Reçu | Taaouniaty" description="Reçu de commande Taaouniaty." />
        <div className="receipt-loading">
          <div className="receipt-spinner" />
          <p>Chargement du reçu...</p>
        </div>
      </div>
    )
  }

  if (error || !receipt) {
    return (
      <div className="receipt-page">
        <SeoHead noindex title="Reçu introuvable | Taaouniaty" description="Ce reçu n'est pas disponible." />
        <div className="receipt-error">
          <h2>{error || 'Reçu introuvable'}</h2>
          <p>Ce lien a peut-être expiré ou n'est pas valide.</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="receipt-page"
      style={{
        '--catalog-primary-color': receipt.catalog_primary_color || '#ff8c00',
        '--catalog-secondary-color': receipt.catalog_secondary_color || '#ff6b00',
      }}
    >
      <SeoHead
        noindex
        title={`Reçu ${receiptRefDisplay} | Taaouniaty`}
        description={`Reçu de commande ${receiptRefDisplay} sur Taaouniaty.`}
        url={buildAbsoluteUrl(window.location.pathname)}
      />

      {receipt.reviews_enabled && receipt.can_leave_review && (
        <p className="receipt-review-hint no-print">
          Laissez votre avis en bas de la page votre retour compte.
        </p>
      )}

      <div className="take-receipt" ref={receiptRef}>
        {/* Top action icons */}
        <div className="take-receipt-actions no-print">
          <button type="button" className="take-receipt-icon-btn" onClick={handlePrint} title="Imprimer">
            <i className="fas fa-print" aria-hidden />
          </button>
          <button type="button" className="take-receipt-icon-btn" onClick={downloadPDF} title="Télécharger le reçu">
            <i className="fas fa-download" aria-hidden />
          </button>
          <button type="button" className="take-receipt-icon-btn" onClick={shareReceipt} title="Partager">
            <i className="fas fa-share-alt" aria-hidden />
          </button>
        </div>
        {shareNote && (
          <p className="receipt-share-note no-print">{shareNote}</p>
        )}

        {/* Business name + phone */}
        <header className="take-receipt-header">
          {isShopReceipt ? (
            <div className="take-receipt-lockup">
              <ReceiptBrandMark />
              <span className="take-receipt-lockup-titles">
                <strong>taaouniaty</strong>
                <small>Marketplace</small>
              </span>
            </div>
          ) : (
            <h1 className="take-receipt-business">{receipt.cooperative_name || 'Coopérative'}</h1>
          )}
          {receipt.cooperative_phone && (
            <a href={`tel:${receipt.cooperative_phone}`} className="take-receipt-phone">
              {receipt.cooperative_phone}
            </a>
          )}
        </header>

        {/* Invoice No + Order date */}
        <div className="take-receipt-invoice-meta">
          <div className="take-receipt-invoice-row">
            <span className="take-receipt-invoice-label">Reçu n°</span>
            <span className="take-receipt-invoice-num">#{receipt.receipt_reference || receiptRefDisplay}</span>
          </div>
          <div className="take-receipt-invoice-row">
            <span className="take-receipt-invoice-label">Date de commande</span>
            <span className="take-receipt-invoice-date">{formatDateWithTz(receipt.created_at)}</span>
          </div>
        </div>

        {/* Items */}
        <section className="take-receipt-section">
          <h3 className="take-receipt-section-title">Articles</h3>
          <ul className="take-receipt-items">
            {receipt.items.map((item, i) => {
              const addonsPerUnit = Array.isArray(item.addons) && item.addons.length > 0
                ? item.addons.reduce((sum, a) => sum + Number(a.price_delta || 0), 0)
                : 0
              const basePerUnit = Number(item.unit_price || 0) - addonsPerUnit
              const quantity = Number(item.quantity || 1)
              const productLinePrice = basePerUnit * quantity
              return (
                <li key={i} className="take-receipt-item">
                  <div className="take-receipt-item-main">
                    <span className="take-receipt-item-desc">
                      {item.quantity}x {item.title}
                    </span>
                    <span className="take-receipt-item-price">{formatNum(productLinePrice)} DH</span>
                  </div>
                  {item.addons && item.addons.length > 0 && (
                    <ul className="take-receipt-item-addons">
                      {item.addons.map((addon, j) => (
                        <li key={j} className="take-receipt-addon">
                          <span className="take-receipt-addon-name">+ {addon.name}</span>
                          {addon.price_delta != null && Number(addon.price_delta) !== 0 ? (
                            <span className="take-receipt-addon-price">
                              {Number(addon.price_delta) > 0 ? `+${formatNum(addon.price_delta)}` : formatNum(addon.price_delta)} DH
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        </section>

        {/* Totals */}
        <section className="take-receipt-totals">
          <div className="take-receipt-totals-row">
            <span>Total articles ({receipt.items.length})</span>
            <span>{formatNum(receipt.subtotal)} DH</span>
          </div>
          <div className="take-receipt-totals-row">
            <span>{receipt.delivery_fee > 0 ? 'Frais de livraison' : 'Livraison'}</span>
            <span>{receipt.delivery_fee > 0 ? `${formatNum(receipt.delivery_fee)} DH` : 'Gratuite'}</span>
          </div>
          <div className="take-receipt-totals-row take-receipt-grand-total">
            <span>Total</span>
            <span>{formatNum(receiptTotal)} DH</span>
          </div>
        </section>

        {/* Order Details: Customer + Delivery */}
        <section className="take-receipt-section take-receipt-order-details">
          <h3 className="take-receipt-section-title">Détails de la commande</h3>
          {receipt.guest && (
            <>
              <div className="take-receipt-detail-block">
                <p className="take-receipt-detail-label">Client</p>
                <p className="take-receipt-detail-value">{receipt.guest.full_name}</p>
                <a href={`tel:${receipt.guest.phone}`} className="take-receipt-detail-link">
                  {receipt.guest.phone}
                </a>
              </div>
              <div className="take-receipt-detail-block">
                <p className="take-receipt-detail-label">Livraison</p>
                <p className="take-receipt-detail-link take-receipt-address">
                  {[receipt.guest.city, receipt.guest.address].filter(Boolean).join(' — ') || '—'}
                </p>
              </div>
            </>
          )}
        </section>

        {/* Catalog link */}
        {catalogUrl && (
          <div className="take-receipt-catalog-link no-print">
            <p className="take-receipt-catalog-label">
              {isShopReceipt ? 'Retournez au marketplace' : 'Retournez au catalogue'}
            </p>
            <a href={catalogUrl} target="_blank" rel="noopener noreferrer" className="take-receipt-link">
              {catalogUrl}
            </a>
          </div>
        )}

        {/* Footer */}
        <footer className="take-receipt-footer">
          <p>Taaouniaty</p>
        </footer>
      </div>

      {receipt.reviews_enabled && (
        <div
          className="receipt-review-block no-print"
          style={{
            '--catalog-primary-color': receipt.catalog_primary_color || '#ff8c00',
            '--catalog-secondary-color': receipt.catalog_secondary_color || '#ff6b00',
          }}
        >
          {receipt.has_review ? (
            <p className="receipt-review-success">
              Merci ! Vous avez déjà laissé un avis pour cette commande.
            </p>
          ) : receipt.can_leave_review ? (
            <>
              <h3>Comment s&apos;est passée votre commande ?</h3>
              <p>Partagez votre expérience avec {receipt.cooperative_name || 'cette coopérative'}.</p>
              <form className="receipt-review-form" onSubmit={handleSubmitReview}>
                <InteractiveStarRating
                  value={reviewRating}
                  onChange={setReviewRating}
                  disabled={reviewSubmitting}
                />
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  rows={3}
                  placeholder="Ex. Livraison rapide, produits de qualité… Merci !"
                />
                {reviewMessage && (
                  <p className={`coop-reviews-form__msg coop-reviews-form__msg--${reviewMessage.type}`}>
                    {reviewMessage.text}
                  </p>
                )}
                <button
                  type="submit"
                  className="coop-reviews-btn-primary"
                  disabled={reviewSubmitting}
                >
                  {reviewSubmitting ? 'Envoi...' : 'Laisser un avis'}
                </button>
              </form>
            </>
          ) : null}
        </div>
      )}

      <div className="receipt-page-cta no-print">
        {isShopReceipt ? (
          <Link to="/marketplace" className="receipt-page-cta-btn">
            Retour au marketplace
          </Link>
        ) : (
          <Link to="/register" className="receipt-page-cta-btn">
            Créez votre catalogue
          </Link>
        )}
      </div>
    </div>
  )
}
